import { useEffect, useState } from 'react';
import { Typography } from '@material-tailwind/react';
import { motion } from 'framer-motion';
import { formatPhoneNumberAsYouType } from 'src/modules/shared/utils/formatting/phone.util';
import { formatDuration } from 'src/modules/shared/utils/formatting/time.util';
import STATES from 'states/index';
import { usePhoneContext } from 'src/contexts/phone-context';
import { AcceptCallButton } from '../../../atoms/phones/buttons/accept-call-button';
import { EndCallButton } from '../../../atoms/phones/buttons/end-call-button';
import { MuteButton } from '../../../atoms/phones/buttons/mute-button';
import { SettingsButton } from '../../../atoms/phones/buttons/settings-button';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';

export const ContainerPhone: React.FC = () => {
  const [duration, setDuration] = useState(0);

  const { phoneState, isMuted, setIsMuted } = usePhoneContext();
  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();

  useEffect(() => {
    if (phoneState !== STATES.PHONE.ESTABLISHED) return;

    const timer = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [phoneState]);

  useEffect(() => {
    if (phoneState === STATES.PHONE.ESTABLISHED) return;

    setDuration(0);
  }, [phoneState]);

  if (
    providerLoading ||
    !callProvider ||
    callProvider.loading ||
    callProvider.error
  ) {
    return null;
  }

  const handleMute = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    callProvider.callActions.muteCall(newMutedState);
  };

  const currentNumber =
    phoneState === STATES.PHONE.INCOMING
      ? callProvider.callerNumber
      : callProvider.onCallNumber;
  const displayNumber = currentNumber
    ? formatPhoneNumberAsYouType(currentNumber)
    : '';

  const renderCallControls = () => {
    if (phoneState === STATES.PHONE.INCOMING) {
      return (
        <div className="flex items-center gap-2">
          <AcceptCallButton onClick={callProvider.callActions.acceptCall} />
          <EndCallButton onClick={callProvider.callActions.rejectCall} />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <MuteButton onClick={handleMute} isMuted={isMuted} />
        <EndCallButton onClick={callProvider.callActions.endCall} />
        <SettingsButton disabled onClick={() => {}} />
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="absolute top-4 left-0 right-0 mx-auto flex justify-center z-10"
    >
      <div className="bg-white rounded-lg shadow-lg p-3 flex items-center justify-between gap-4 min-w-[250px]">
        <div className="flex flex-col">
          <Typography className="text-sm font-medium text-gray-900">
            {displayNumber}
          </Typography>
          {phoneState === STATES.PHONE.ESTABLISHED && (
            <Typography className="text-sm text-gray-500">
              {formatDuration(duration)}
            </Typography>
          )}
          {phoneState === STATES.PHONE.INCOMING && (
            <Typography className="text-sm text-gray-500">
              Incoming call...
            </Typography>
          )}
        </div>

        {renderCallControls()}
      </div>
    </motion.div>
  );
};
